import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { Reveal, SectionHead, EASE } from "./Reveal";

const VIDEO_SRC = "/media/frontlines-railway-hospital.mp4";
const POSTER = "/media/frontlines-railway-hospital-poster.jpg";

export default function Frontlines() {
    const videoRef = useRef(null);
    const [playing, setPlaying] = useState(false);

    const toggle = () => {
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) v.play();
        else v.pause();
    };

    return (
        <section
            id="frontlines"
            data-testid="frontlines-section"
            className="relative overflow-hidden py-24 lg:py-32"
        >
            <div className="pointer-events-none absolute right-[-8%] top-10 h-[460px] w-[460px] rounded-full bg-accent/5 blur-[150px]" />
            <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <SectionHead
                        label="On the Frontlines"
                        title="Our work, in action."
                    />
                    <Reveal delay={0.15}>
                        <p className="max-w-sm text-sm leading-relaxed text-slate-400">
                            Video and pictorial demonstrations from real project
                            sites — theatres and sterile departments we have
                            designed and built.
                        </p>
                    </Reveal>
                </div>

                <div className="mt-14 grid items-start gap-6 lg:grid-cols-12">
                    <Reveal className="lg:col-span-8" y={32}>
                        <div
                            data-testid="frontlines-video-card"
                            className="group relative overflow-hidden rounded-2xl border border-line bg-ink"
                        >
                            <video
                                ref={videoRef}
                                data-testid="frontlines-video"
                                src={VIDEO_SRC}
                                poster={POSTER}
                                controls
                                playsInline
                                preload="metadata"
                                onPlay={() => setPlaying(true)}
                                onPause={() => setPlaying(false)}
                                onClick={toggle}
                                className="aspect-video w-full cursor-pointer object-cover"
                            />
                            {!playing && (
                                <motion.button
                                    initial={{ opacity: 0, scale: 0.85 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.4, ease: EASE }}
                                    data-testid="frontlines-play-button"
                                    onClick={toggle}
                                    aria-label="Play project video"
                                    className="absolute inset-0 flex items-center justify-center bg-ink/40 transition-colors duration-300 hover:bg-ink/30"
                                >
                                    <span className="flex h-20 w-20 items-center justify-center rounded-full border border-accent/60 bg-ink/70 text-accent backdrop-blur-md transition-transform duration-300 hover:scale-110">
                                        <Play className="ml-1 h-8 w-8 fill-current" />
                                    </span>
                                </motion.button>
                            )}
                        </div>
                    </Reveal>

                    <Reveal className="lg:col-span-4" y={32} delay={0.1}>
                        <div className="flex h-full flex-col rounded-2xl border border-line bg-panel/50 p-7 sm:p-8">
                            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-accent">
                                Project
                            </span>
                            <h3
                                data-testid="frontlines-project-title"
                                className="mt-3 font-display text-xl font-bold leading-snug text-white sm:text-2xl"
                            >
                                Divisional Railway Hospital, Chakradharpur,
                                S.E. Railways, India
                            </h3>

                            <div className="my-6 border-t border-line" />

                            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">
                                Project Director
                            </span>
                            <p
                                data-testid="frontlines-director"
                                className="mt-3 font-display text-lg font-bold text-white"
                            >
                                Prof. Rajat Roy, BME
                            </p>
                            <p className="mt-1 text-sm text-slate-400">
                                Technical Consultant in Hospital Management
                            </p>

                            <div className="mt-auto pt-8">
                                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-600">
                                    Video · Pictorial Demonstration
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
